import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box } from '@mui/material';
import { KVOX_BRAND } from './identity.js';
import { MARK_ACCENT, MARK_ARMS, MARK_BARS, MARK_CANVAS, MARK_INK, MARK_PLATE_RADIUS, MARK_STEM, MARK_STROKE, } from './mark-geometry.js';
/**
 * The KVox brand mark: a white K and five amber waveform bars on the brand plate,
 * drawn as inline SVG.
 *
 * It needs no network request and no `public/` folder, which is why a host
 * without a web server (KVox Desktop) can show the same mark as the web app. The
 * geometry is `mark-geometry.ts` (the favicon crop of `assets/favicon.svg`); the
 * plate colour is `KVOX_BRAND.themeColor`.
 *
 * The plate stays brand-coloured in both themes. It is the brand's own ground,
 * the same plate as the launcher icon and the tab favicon, so it does not follow
 * `background.paper`.
 *
 * `borderRadius` is `size * 0.22`, the plate's own corner ratio, set on the
 * element so a box painted behind it is clipped to the same corner. MUI's `sx`
 * multiplies a number by `theme.shape.borderRadius`, exactly as the web app's
 * former `<img>` mark did, so the clip is as it has always been.
 *
 * @example
 * ```tsx
 * <BrandMark size={56} />
 * ```
 *
 * @stability experimental
 */
export function BrandMark({ size = 28, title, sx }) {
    const labelled = title !== undefined && title !== '';
    const own = { borderRadius: size * 0.22 };
    return (_jsxs(Box, { component: "svg", xmlns: "http://www.w3.org/2000/svg", width: size, height: size, viewBox: `0 0 ${MARK_CANVAS} ${MARK_CANVAS}`, role: labelled ? 'img' : undefined, "aria-label": labelled ? title : undefined, "aria-hidden": labelled ? undefined : true, focusable: "false", "data-testid": "kvox-brand-mark", sx: [own, ...(Array.isArray(sx) ? sx : sx === undefined ? [] : [sx])], children: [labelled ? _jsx("title", { children: title }) : null, _jsx("rect", { width: MARK_CANVAS, height: MARK_CANVAS, rx: MARK_PLATE_RADIUS, fill: KVOX_BRAND.themeColor }), _jsx("g", { fill: MARK_INK, children: _jsx("rect", { x: MARK_STEM.x, y: MARK_STEM.y, width: MARK_STEM.width, height: MARK_STEM.height, rx: MARK_STEM.rx }) }), _jsx("g", { stroke: MARK_INK, strokeWidth: MARK_STROKE, strokeLinecap: "round", fill: "none", children: MARK_ARMS.map((arm) => (_jsx("line", { x1: arm.x1, y1: arm.y1, x2: arm.x2, y2: arm.y2 }, arm.y2))) }), _jsx("g", { fill: MARK_ACCENT, children: MARK_BARS.map((bar) => (_jsx("rect", { x: bar.x, y: bar.y, width: bar.width, height: bar.height, rx: bar.rx }, bar.x))) })] }));
}
